import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './LandingPage.css';

const Arrow = () => <span aria-hidden="true">↗</span>;

function LandingPage() {
  const [loginOpen, setLoginOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  // GrapesJS published content
  const [paginaHtml, setPaginaHtml] = useState<string | null>(null);
  const [paginaCss, setPaginaCss] = useState<string>('');

  // Check for OAuth error in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('oauth_error');
    if (oauthError) {
      setLoginError(oauthError);
      setLoginOpen(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const loginRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  // Fetch GrapesJS published page
  useEffect(() => {
    const controller = new AbortController();
    fetch('/api/conteudo/pagina/landing-page', { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => {
        if (data.html && data.html.trim()) {
          setPaginaHtml(data.html);
          setPaginaCss(data.css || '');
        }
      })
      .catch(() => {
        /* fallback to JSX */
      });
    return () => controller.abort();
  }, []);

  // Header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setHeaderScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle clicks inside the bank HTML content (open login on specific elements)
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const handleClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest(
        '.header-cta, .client-access-card, [data-open-login]',
      );
      if (target) {
        e.preventDefault();
        setLoginOpen(true);
      }
    };
    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [paginaHtml]);

  // Close login modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (loginRef.current && !loginRef.current.contains(e.target as Node)) {
        setLoginOpen(false);
      }
    };
    if (loginOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [loginOpen]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, senha }),
      });
      const data = await res.json();

      if (!res.ok) {
        setLoginError(data.error?.message || 'Credenciais inválidas.');
        return;
      }

      login(data.token, data.usuario);
      const isMainDomain =
        window.location.hostname === 'vavassori.arq.br' ||
        window.location.hostname === 'www.vavassori.arq.br';

      let destino = '/';

      if (data.requerTrocaSenha) {
        destino = '/trocar-senha';
      } else {
        switch (data.usuario.tipo) {
          case 'Cliente':
            destino = '/empreendimentos';
            break;
          case 'Arquiteto':
            destino = '/painel';
            break;
          case 'Assistente':
            destino = '/admin';
            break;
          case 'Master':
            destino = '/admin/conteudo';
            break;
        }
      }

      if (isMainDomain) {
        window.location.href = `https://app.vavassori.arq.br${destino}?token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.usuario))}`;
      } else {
        navigate(destino);
      }
    } catch {
      setLoginError('Erro de conexão. Tente novamente.');
    } finally {
      setLoginLoading(false);
    }
  };

  // Login modal overlay
  const loginModal = loginOpen && (
    <div className="login-overlay" onClick={() => setLoginOpen(false)}>
      <div
        className="login-panel"
        ref={loginRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="login-panel-copy">
          <p className="login-kicker">Área exclusiva</p>
          <h2>
            Acompanhe cada etapa,<br />
            <em>sempre por perto.</em>
          </h2>
          <p>
            Acesse sua área exclusiva para visualizar o progresso da obra,
            relatórios fotográficos e documentação completa do seu projeto.
          </p>
        </div>
        <div className="login-panel-form">
          <p className="login-form-title">Entrar</p>
          <form onSubmit={handleLogin} noValidate>
            {loginError && <p className="login-error">{loginError}</p>}
            <label htmlFor="login-email">E-mail</label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <label htmlFor="login-senha">Senha</label>
            <input
              id="login-senha"
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button type="submit" disabled={loginLoading}>
              {loginLoading ? 'Entrando...' : 'Acessar'} <Arrow />
            </button>
          </form>
          <a href="/esqueci-senha" className="login-forgot">
            Esqueci minha senha
          </a>
          <div className="login-divider">
            <span>ou</span>
          </div>
          <div className="login-social-buttons">
            <button
              className="login-social google"
              onClick={() => {
                window.location.href = '/api/auth/oauth/google';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
                <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" />
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.581-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" />
              </svg>
              Entrar com Google
            </button>
            <button
              className="login-social facebook"
              onClick={() => {
                window.location.href = '/api/auth/oauth/facebook';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#fff" d="M18 9a9 9 0 1 0-10.406 8.89v-6.288H5.309V9h2.285V7.017c0-2.255 1.343-3.501 3.4-3.501.984 0 2.014.176 2.014.176v2.215h-1.135c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.399 2.602h-2.097v6.289A9.002 9.002 0 0 0 18 9z" />
              </svg>
              Entrar com Facebook
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Header (React-controlled — always visible, new editorial style)
  const header = (
    <header className={`site-header${headerScrolled ? ' header-scrolled' : ''}`}>
      <Link className="brand" to="/" aria-label="Vavassori Designer — início">
        <img src="/images/logo.png" alt="Vavassori Designer" className="brand-logo" />
      </Link>

      <nav className="desktop-nav" aria-label="Navegação principal">
        <a href="#projetos">Projetos</a>
        <a href="#servicos">Serviços</a>
        <a href="#area-cliente">Área do cliente</a>
        <a href="#contato">Contato</a>
      </nav>

      <button
        className="header-cta"
        onClick={() => setLoginOpen(true)}
        aria-haspopup="dialog"
      >
        Acessar Sistema <Arrow />
      </button>
    </header>
  );

  // If GrapesJS HTML is published, render header + custom content + login modal
  if (paginaHtml) {
    return (
      <>
        <div className="landing-page">
          {header}
          <style dangerouslySetInnerHTML={{ __html: paginaCss }} />
          <div ref={contentRef} dangerouslySetInnerHTML={{ __html: paginaHtml }} />
        </div>

        {loginModal}

        <a
          href="https://wa.me/5519982976709?text=Ol%C3%A1%2C%20gostaria%20de%20informa%C3%A7%C3%B5es%2C%20aguardo%20contato."
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-float"
          aria-label="Fale conosco pelo WhatsApp"
        >
          <svg viewBox="0 0 32 32" width="28" height="28" fill="currentColor" aria-hidden="true">
            <path d="M16.004 0C7.177 0 .004 7.173.004 16.001c0 2.823.737 5.577 2.138 8.005L.012 32l8.187-2.088A15.93 15.93 0 0 0 16.004 32C24.831 32 32 24.827 32 16.001 32 7.173 24.831 0 16.004 0zm0 29.26a13.21 13.21 0 0 1-6.743-1.847l-.484-.287-5.014 1.313 1.338-4.885-.315-.5A13.17 13.17 0 0 1 2.744 16C2.744 8.683 8.686 2.74 16.004 2.74S29.26 8.683 29.26 16c0 7.319-5.938 13.26-13.256 13.26zm7.268-9.928c-.398-.2-2.356-1.163-2.722-1.296-.365-.133-.631-.2-.897.199-.266.398-1.031 1.296-1.264 1.562-.232.266-.465.299-.863.1-.398-.2-1.681-.62-3.203-1.976-1.184-1.055-1.983-2.359-2.215-2.757-.232-.398-.025-.614.175-.812.179-.179.398-.465.597-.697.199-.232.265-.398.398-.664.133-.266.066-.498-.034-.697-.1-.2-.897-2.161-1.229-2.96-.323-.776-.652-.671-.897-.683-.232-.011-.498-.013-.764-.013s-.697.1-1.063.498c-.365.398-1.396 1.364-1.396 3.326 0 1.962 1.43 3.858 1.629 4.124.199.266 2.813 4.293 6.814 6.02.952.411 1.695.656 2.274.84.955.304 1.825.261 2.513.158.767-.114 2.356-.963 2.689-1.894.332-.93.332-1.727.232-1.894-.1-.166-.365-.266-.764-.465z" />
          </svg>
        </a>
      </>
    );
  }

  // Fallback: no content from database — show minimal message
  return (
    <div className="landing-page">
      {header}
      <section className="hero" id="inicio">
        <div className="hero-visual" aria-hidden="true" />
        <div className="hero-content">
          <h1>Vavassori Designer</h1>
          <p className="hero-intro">Carregando...</p>
        </div>
      </section>
      {loginModal}
    </div>
  );
}

export default LandingPage;
